import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon, Film, X, FileText, Loader2, Pencil } from 'lucide-react';
import type { MediaFile, UrlMedia } from './types';

interface Props {
  mediaFiles: MediaFile[];
  setMediaFiles: React.Dispatch<React.SetStateAction<MediaFile[]>>;
  urlMediaItems: UrlMedia[];
  setUrlMediaItems: React.Dispatch<React.SetStateAction<UrlMedia[]>>;
  onEditSavedMedia?: (item: SavedMediaItemRef) => void;
}

interface SavedItem {
  key: string;
  source: 'file' | 'url';
  id: string;
  postMediaIndex?: number;
  filename: string;
  preview?: string;
  type: 'image' | 'video' | 'document';
  contentType?: string;
  persisted: boolean;
}

export interface SavedMediaItemRef {
  id: string;
  mediaId?: string;
  postMediaIndex?: number;
  filename: string;
  preview?: string;
  type: 'image' | 'video' | 'document';
  persisted: boolean;
}

function truncate(value: string, max = 42): string {
  if (!value) {
    return '';
  }
  if (value.length <= max) {
    return value;
  }
  return `${value.slice(0, max - 1)}…`;
}

export const SavedMediaTab: React.FC<Props> = ({
  mediaFiles,
  setMediaFiles,
  urlMediaItems,
  setUrlMediaItems,
  onEditSavedMedia,
}) => {
  const { t } = useTranslation();

  const items: SavedItem[] = [];

  mediaFiles.forEach((mf) => {
    items.push({
      key: `file-${mf.id}`,
      source: 'file',
      id: mf.id,
      filename: mf.file?.name || t('composer_saved_file_label', 'Upload'),
      preview: mf.preview,
      type: mf.type,
      contentType: mf.file?.type,
      persisted: false,
    });
  });

  urlMediaItems
    .filter((item) => item.status === 'done')
    .forEach((um, idx) => {
      const isDoc = um.contentType?.includes('pdf') || um.type !== 'image' && um.type !== 'video';
      items.push({
        key: `url-${um.id}`,
        source: 'url',
        id: um.id,
        postMediaIndex: idx,
        filename: um.filename || um.url || t('composer_saved_untitled', 'Untitled media'),
        preview: um.url,
        type: isDoc ? 'document' : um.type,
        contentType: um.contentType,
        persisted: Boolean(um.mediaId),
      });
    });

  const handleRemove = (item: SavedItem) => {
    if (item.source === 'file') {
      setMediaFiles((prev) => prev.filter((mf) => mf.id !== item.id));
      return;
    }
    setUrlMediaItems((prev) => prev.filter((um) => um.id !== item.id));
  };

  if (items.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <ImageIcon className="w-10 h-10 text-gray-300 mb-2" />
        <p className="text-sm text-gray-500">
          {t('composer_saved_empty', 'No media attached yet')}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          {t(
            'composer_saved_empty_hint',
            'Attach media from Library, Upload, URL, Cloud or AI Generate.',
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-2 flex-shrink-0">
        <p className="text-xs font-medium text-gray-600">
          {t('composer_saved_count', '{{count}} attached', { count: items.length })}
        </p>
      </div>
      <div className="flex-1 overflow-y-auto min-h-0 space-y-1 pr-1">
        {items.map((item) => {
          const isVideo = item.type === 'video';
          const isDoc = item.type === 'document';
          const canEdit = item.persisted && !isDoc && Boolean(onEditSavedMedia);
          return (
            <div
              key={item.key}
              className="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg border border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50/50 transition-all"
            >
              <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 relative border border-gray-100 bg-gray-50">
                {isDoc ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-400" />
                  </div>
                ) : isVideo ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <Film className="w-5 h-5 text-purple-400" />
                  </div>
                ) : item.preview ? (
                  <img
                    src={item.preview}
                    alt={item.filename}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-5 h-5 text-gray-300" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-900 truncate">
                  {truncate(item.filename, 48)}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {item.persisted ? (
                    <span
                      className="text-[10px] font-semibold uppercase tracking-wider text-green-600"
                      title={t(
                        'composer_saved_tooltip_saved',
                        'Saved to your media library',
                      )}
                    >
                      {t('composer_saved_status_persisted', 'Saved')}
                    </span>
                  ) : (
                    // Upload finished but the media record hasn't been
                    // persisted to the library yet — show an active
                    // spinner so the UI reads as "in progress" instead
                    // of a stuck "PENDING" badge with nothing to click.
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-600"
                      aria-live="polite"
                      aria-busy="true"
                      title={t(
                        'composer_saved_tooltip_saving',
                        'Saving to your media library — this usually takes a few seconds',
                      )}
                    >
                      <Loader2
                        className="w-2.5 h-2.5 animate-spin"
                        aria-hidden="true"
                      />
                      {t('composer_saved_status_saving', 'Saving…')}
                    </span>
                  )}
                  {item.contentType && (
                    <>
                      <span className="text-gray-200">·</span>
                      <span className="text-[10px] text-gray-400 uppercase">
                        {item.contentType.split('/').pop()}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() =>
                  onEditSavedMedia?.({
                    id: item.id,
                    mediaId: item.source === 'url'
                      ? urlMediaItems.find((um) => um.id === item.id)?.mediaId
                      : undefined,
                    postMediaIndex: item.postMediaIndex,
                    filename: item.filename,
                    preview: item.preview,
                    type: item.type,
                    persisted: item.persisted,
                  })
                }
                aria-label={t('composer_saved_edit_aria', 'Edit {{name}}', {
                  name: item.filename,
                })}
                title={
                  canEdit
                    ? t('composer_saved_edit_tooltip', 'Edit this asset')
                    : isDoc
                      ? t(
                          'composer_saved_edit_unsupported_tooltip',
                          'Documents cannot be edited in the media editor',
                        )
                    : t('composer_saved_edit_disabled_tooltip', 'Save media first to edit it')
                }
                disabled={!canEdit}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors flex-shrink-0 disabled:opacity-40 disabled:hover:text-gray-400 disabled:hover:bg-transparent"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => handleRemove(item)}
                aria-label={t('composer_saved_remove_aria', 'Remove {{name}}', {
                  name: item.filename,
                })}
                title={t('composer_saved_remove_tooltip', 'Remove from post')}
                className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
